import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import toast from 'react-hot-toast'
import { 
  ClipboardDocumentCheckIcon, 
  ClockIcon, 
  CheckCircleIcon,
  ArrowLeftIcon,
  XCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline'
import { SkillsPicker } from '../components/SkillsPicker'

interface Survey {
  id: string
  title: string
  description: string
  questions: any[]
  status: string
  deadline?: string
  created_at: string
}

// Helper function to check if survey is expired
const isSurveyExpired = (survey: Survey): boolean => {
  if (!survey.deadline) return false
  return new Date(survey.deadline) < new Date()
}

// Helper function to convert display values to database enum values
const toDbValue = (displayValue: any, field: 'employment_status' | 'current_employment_status' | 'employer_type' | 'job_relevance' | 'salary_range' | 'employment_length'): string | number | null => {
  if (!displayValue && displayValue !== 0) return null
  
  // If it's not a string (e.g., a number from a rating question), return as-is
  if (typeof displayValue !== 'string') {
    return displayValue
  }
  
  if (field === 'employment_status' || field === 'current_employment_status') {
    const map: Record<string, string> = {
      'Employed': 'employed',
      'Unemployed': 'unemployed',
      'Self-Employed': 'self-employed',
      'Studying': 'studying',
    }
    return map[displayValue] || displayValue.toLowerCase().replace(/ /g, '_')
  }
  
  if (field === 'employer_type') {
    const map: Record<string, string> = {
      'Private': 'private',
      'Public': 'public',
      'Self-Employed': 'self-employed',
      'Non-Profit': 'non-profit',
      'Government': 'government'
    }
    return map[displayValue] || displayValue.toLowerCase().replace(/\//g, '_').replace(/\s+/g, '_').replace(/_+/g, '_')
  }
  
  if (field === 'job_relevance') {
    const map: Record<string, string> = {
      'Highly Relevant': 'highly_relevant',
      'Relevant': 'relevant',
      'Somewhat Relevant': 'somewhat_relevant',
      'Not Relevant': 'not_relevant'
    }
    return map[displayValue] || displayValue.toLowerCase().replace(/ /g, '_')
  }
  
  if (field === 'salary_range') {
    const map: Record<string, string> = {
      'Under ₱20,000': 'under_20k',
      'Under 20,000': 'under_20k',
      'Under 20000': 'under_20k',
      '₱20,000 - ₱40,000': '20k_40k',
      '20,000 - 40,000': '20k_40k',
      '20000 - 40000': '20k_40k',
      '₱40,000 - ₱60,000': '40k_60k',
      '40,000 - 60,000': '40k_60k',
      '40000 - 60000': '40k_60k',
      '₱60,000 - ₱80,000': '60k_80k',
      '60,000 - 80,000': '60k_80k',
      '60000 - 80000': '60k_80k',
      '₱80,000 - ₱100,000': '80k_100k',
      '80,000 - 100,000': '80k_100k',
      '80000 - 100000': '80k_100k',
      'Over ₱100,000': 'over_100k',
      'Over 100,000': 'over_100k',
      'Over 100000': 'over_100k'
    }
    
    return map[displayValue] || displayValue.toLowerCase().replace(/[₱,\s]/g, '').replace(/-/g, '_')
  }
  
  if (field === 'employment_length') {
    const map: Record<string, string> = {
      'Less than a month': 'less_than_month',
      '1-6 months': '1_6_months',
      '6-12 months': '6_12_months',
      '1-2 years': '1_2_years',
      '2-5 years': '2_5_years',
      'More than 5 years': 'more_than_5_years'
    }
    return map[displayValue] || displayValue
  }
  
  return displayValue
}

const SurveyPage: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const surveyIdFromUrl = searchParams.get('id')
  const filterParam = searchParams.get('filter') || 'all'
  
  const [surveys, setSurveys] = useState<Survey[]>([])
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null)
  const [answers, setAnswers] = useState<Record<string, any>>({})
  const [completedSurveys, setCompletedSurveys] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [isViewingResponse, setIsViewingResponse] = useState(false)
  const [filter, setFilter] = useState<'all' | 'available' | 'completed'>(
    filterParam as 'all' | 'available' | 'completed'
  )

  useEffect(() => {
    fetchSurveys()
    fetchCompletedSurveys()

    // Set up real-time subscription for survey responses
    // This will update when admin deletes responses during redeployment
    const subscription = supabaseClient
      .channel('survey_responses_alumni_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'survey_responses' },
        (payload) => {
          console.log('Survey responses changed:', payload)
          // Refetch completed surveys when responses are modified
          setTimeout(() => {
            fetchCompletedSurveys()
          }, 500) // Small delay to ensure DB consistency
        }
      )
      .subscribe()

    // Also refresh when page becomes visible (handles cases where real-time doesn't work)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page became visible, refreshing surveys and completed surveys...')
        fetchSurveys()
        fetchCompletedSurveys()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      subscription.unsubscribe()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [user])

  useEffect(() => {
    if (surveyIdFromUrl && surveys.length > 0) {
      const survey = surveys.find(s => s.id === surveyIdFromUrl)
      if (survey) {
        handleSelectSurvey(survey)
      }
    }
  }, [surveyIdFromUrl, surveys])

  const fetchSurveys = async () => {
    if (!user) {
      console.log('fetchSurveys: No user, returning')
      return
    }

    try {
      console.log('\n=== FETCH SURVEYS START ===')
      console.log('User ID:', user.id)
      setLoading(true)
      
      // Fetch alumni profile to get program, department, and graduation year
      const { data: profile, error: profileError } = await supabaseClient
        .from('alumni_profiles')
        .select('program, department, year_graduated')
        .eq('user_id', user.id)
        .single()

      if (profileError) {
        console.error('Error fetching profile:', profileError)
        toast.error('Unable to fetch your profile')
        return
      }

      // Get alumnus details
      const programCode = profile?.program
      const department = profile?.department
      const yearGraduated = profile?.year_graduated

      console.log('Alumni profile:', profile)
      console.log('Alumni program code:', programCode)
      console.log('Alumni department:', department)
      console.log('Alumni year graduated:', yearGraduated)

      // Fetch ALL surveys (we'll filter by program and handle status in UI)
      // Don't filter by status here to ensure completed surveys always show
      const { data: surveysData, error: surveysError } = await supabaseClient
        .from('surveys')
        .select('*')
        .neq('status', 'archived') // Only exclude archived surveys
        .order('created_at', { ascending: false })

      if (surveysError) {
        console.error('Error fetching surveys:', surveysError)
        toast.error('Unable to fetch surveys')
        return
      }

      console.log('RAW surveys from DB (count):', surveysData?.length)
      console.log('RAW surveys data:', surveysData?.map(s => ({ id: s.id, title: s.title, status: s.status, target: s.target_programs })))

      // Filter surveys based on department, program, and year targeting
      const filteredSurveys = surveysData.filter((survey: any) => {
        // Check department targeting
        const hasDeptTarget = survey.target_departments && survey.target_departments.length > 0
        const deptMatch = !hasDeptTarget || (department && survey.target_departments.includes(department))
        
        // Check program targeting
        const hasProgTarget = survey.target_programs && survey.target_programs.length > 0
        const progMatch = !hasProgTarget || (programCode && survey.target_programs.includes(programCode))
        
        // Check year/batch targeting
        const hasYearTarget = survey.target_batch && survey.target_batch.length > 0
        const yearMatch = !hasYearTarget || (yearGraduated && survey.target_batch.includes(yearGraduated))
        
        // Log for debugging
        if (hasProgTarget || hasDeptTarget || hasYearTarget) {
          console.log(`Survey "${survey.title}":`, {
            dept: hasDeptTarget ? `targets [${survey.target_departments}], alumni: "${department}", match: ${deptMatch}` : 'no dept filter',
            prog: hasProgTarget ? `targets [${survey.target_programs}], alumni: "${programCode}", match: ${progMatch}` : 'no prog filter',
            year: hasYearTarget ? `targets [${survey.target_batch}], alumni: "${yearGraduated}", match: ${yearMatch}` : 'no year filter',
            finalResult: deptMatch && progMatch && yearMatch
          })
        }
        
        // All checks must pass
        return deptMatch && progMatch && yearMatch
      })

      console.log('Filtered surveys:', filteredSurveys.map(s => ({ title: s.title, status: s.status, id: s.id })))
      
      // Auto-close expired active surveys
      const expiredSurveys = filteredSurveys.filter(s => s.status === 'active' && isSurveyExpired(s))
      
      if (expiredSurveys.length > 0) {
        console.log(`Auto-closing ${expiredSurveys.length} expired surveys:`, expiredSurveys.map(s => s.title))
        
        const { error: updateError } = await supabaseClient
          .from('surveys')
          .update({ status: 'closed' })
          .in('id', expiredSurveys.map(s => s.id))
        
        if (updateError) {
          console.error('Error auto-closing expired surveys:', updateError)
        } else {
          // Update local state to reflect closed status
          const updatedSurveys = filteredSurveys.map(s => 
            expiredSurveys.some(exp => exp.id === s.id) 
              ? { ...s, status: 'closed' } 
              : s
          )
          console.log('Setting surveys after auto-closing:', updatedSurveys.map(s => ({ title: s.title, status: s.status })))
          setSurveys(updatedSurveys)
          return // Early return after updating state
        }
      }
      
      console.log('Setting surveys (final):', filteredSurveys.map(s => ({ title: s.title, status: s.status })))
      setSurveys(filteredSurveys)
    } catch (error) {
      console.error('Error:', error)
      toast.error('An error occurred while loading surveys')
    } finally {
      setLoading(false)
    }
  }

  const fetchCompletedSurveys = async () => {
    if (!user) return

    try {
      console.log('\n=== FETCH COMPLETED SURVEYS START ===')
      console.log('User ID:', user.id)
      
      // Get survey responses - fetch all and filter in JavaScript to avoid query issues
      const { data: responsesData, error: responsesError } = await supabaseClient
        .from('survey_responses')
        .select(`
          survey_id,
          submitted_at,
          surveys!inner (
            id,
            title,
            deadline,
            status
          )
        `)
        .eq('user_id', user.id)

      if (responsesError) {
        console.error('Error fetching completed surveys:', responsesError)
        return
      }

      console.log('RAW responses from DB (count):', responsesData?.length)
      console.log('RAW responses data:', responsesData)

      if (responsesData) {
        
        // Filter out archived surveys only (keep active and closed)
        const filteredResponses = responsesData.filter((r: any) => 
          r.surveys && r.surveys.status !== 'archived'
        )
        
        const validCompletedIds = filteredResponses.map((r: any) => r.survey_id)

        console.log('Filtered responses (active + closed):', filteredResponses)
        console.log('Final completed survey IDs:', validCompletedIds)
        setCompletedSurveys(validCompletedIds)
      }
    } catch (error) {
      console.error('Error fetching completed surveys:', error)
    }
  }

  const handleSelectSurvey = async (survey: Survey, isCompleted: boolean = false) => {
    setSelectedSurvey(survey)
    setIsViewingResponse(isCompleted)
    
    if (isCompleted) {
      // Fetch the survey response for viewing
      try {
        const { data: responseData, error } = await supabaseClient
          .from('survey_responses')
          .select('answers')
          .eq('survey_id', survey.id)
          .eq('user_id', user?.id)
          .single()
        
        if (error) {
          console.error('Error fetching response:', error)
          toast.error('Unable to load your response')
          setAnswers({})
        } else {
          setAnswers(responseData.answers || {})
        }
      } catch (error) {
        console.error('Error:', error)
        setAnswers({})
      }
    } else {
      // Initialize answers for new survey
      setAnswers({})
      const initialAnswers: Record<string, any> = {}
      survey.questions.forEach((q: any) => {
        initialAnswers[q.id] = ''
      })
      setAnswers(initialAnswers)
    }
  }

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }))
  }

  const isQuestionVisible = (question: any): boolean => {
    if (!question.show_if) return true
    const depAnswer = answers[question.show_if.question_id]
    return question.show_if.values.includes(depAnswer)
  }

  const validateAnswers = (): boolean => {
    if (!selectedSurvey) return false

    for (const question of selectedSurvey.questions) {
      if (question.required && isQuestionVisible(question)) {
        const answer = answers[question.id]
        if (answer === undefined || answer === null || answer === '') {
          toast.error(`Please answer the required question: "${question.text || question.question}"`)
          return false
        }
      }
    }
    return true
  }

  const extractAndSavePeoScores = async () => {
    if (!selectedSurvey || !user) return

    try {
      // Collect scores from rating questions that have peo_ids
      const scoreMap: Record<string, { total: number; count: number }> = {}

      selectedSurvey.questions.forEach((question: any) => {
        if (question.type !== 'rating') return
        const answer = answers[question.id]
        if (answer == null) return

        const scale = question.rating_scale || 10
        const normalizedScore = (Number(answer) / scale) * 100

        // Support both peo_ids (array) and legacy peo_id (string)
        const outcomeIds: string[] = question.peo_ids
          ? question.peo_ids
          : question.peo_id
          ? [question.peo_id]
          : []

        outcomeIds.forEach((id: string) => {
          if (!scoreMap[id]) scoreMap[id] = { total: 0, count: 0 }
          scoreMap[id].total += normalizedScore
          scoreMap[id].count += 1
        })
      })

      const scores = Object.entries(scoreMap).map(([peo_id, { total, count }]) => ({
        peo_id,
        score: Math.round(total / count)
      }))

      if (scores.length === 0) return

      const { error } = await supabaseClient.rpc('upsert_alumni_peo_scores', {
        p_scores: scores,
        p_survey_id: selectedSurvey.id
      })

      if (error) {
        console.error('Error saving PEO/PO scores:', error)
        // Don't block survey submission for score extraction failure
      } else {
        console.log(`Saved ${scores.length} PEO/PO scores`)
      }
    } catch (err) {
      console.error('Exception in score extraction:', err)
    }
  }

  const handleSubmit = async () => {
    if (!selectedSurvey || !user) return

    if (!validateAnswers()) {
      return
    }

    try {
      setSubmitting(true)

      // Check if survey has expired
      if (isSurveyExpired(selectedSurvey)) {
        toast.error('This survey has expired and is no longer accepting responses.')
        setSubmitting(false)
        setSelectedSurvey(null)
        fetchSurveys() // Refresh to show updated status
        return
      }

      // Check if survey is closed
      if (selectedSurvey.status === 'closed' || selectedSurvey.status === 'archived') {
        toast.error('This survey is closed and is no longer accepting responses.')
        setSubmitting(false)
        setSelectedSurvey(null)
        fetchSurveys()
        return
      }

      // Check if user has already submitted a response
      const { data: existingResponse } = await supabaseClient
        .from('survey_responses')
        .select('id, submitted_at')
        .eq('survey_id', selectedSurvey.id)
        .eq('user_id', user.id)
        .single()

      if (existingResponse) {
        // User already completed this survey
        console.log('User already completed this survey')
        toast.error('You have already completed this survey.')
        setSubmitting(false)
        return
      }

      // No existing response - submit new response
      const { error } = await supabaseClient
        .from('survey_responses')
        .insert({
          survey_id: selectedSurvey.id,
          user_id: user.id,
          answers: answers,
          submitted_at: new Date().toISOString()
        })

      if (error) {
        console.error('Error submitting survey:', error)
        toast.error('Failed to submit survey. Please try again.')
        setSubmitting(false)
        return
      }

      console.log('Successfully inserted new response')

      // Update alumni profile fields based on question responses
      await updateAlumniProfileFromAnswers()

      // Extract and save PEO/PO scores from rating questions
      await extractAndSavePeoScores()

      toast.success('Survey submitted successfully!')
      setCompletedSurveys(prev => [...prev, selectedSurvey.id])
      setSelectedSurvey(null)
      setAnswers({})
      navigate('/survey')
    } catch (error) {
      console.error('Error:', error)
      toast.error('An error occurred while submitting the survey')
    } finally {
      setSubmitting(false)
    }
  }

  const updateAlumniProfileFromAnswers = async () => {
    if (!selectedSurvey || !user) return

    try {
      // Build profile updates object from questions with profile_field set
      const profileUpdates: Record<string, any> = {}
      
      console.log('[PROFILE UPDATE] Starting profile update process')
      console.log('[PROFILE UPDATE] All answers:', answers)
      console.log('[PROFILE UPDATE] Survey questions:', selectedSurvey.questions)
      
      selectedSurvey.questions.forEach((question: any) => {
        // Skip hidden conditional questions
        if (!isQuestionVisible(question)) return
        
        console.log(`[PROFILE UPDATE] Processing question ${question.id}:`, {
          profile_field: question.profile_field,
          answer: answers[question.id],
          type: question.type
        })
        
        if (question.profile_field && answers[question.id] != null && answers[question.id] !== '') {
          const answer = answers[question.id]
          console.log(`[PROFILE UPDATE] Question has profile_field: ${question.profile_field}, answer:`, answer)
          
          // Handle different field types appropriately
          switch (question.profile_field) {
            case 'employment_status':
            case 'current_employment_status':
            case 'employer_type':
            case 'job_relevance':
            case 'salary_range':
            case 'employment_length':
              // Convert display values to database enum values
              const dbValue = toDbValue(answer, question.profile_field)
              if (dbValue != null) {
                profileUpdates[question.profile_field] = dbValue
              }
              break
            case 'further_studies':
            case 'skills_acquired':
            case 'career_progression':
            case 'job_search_methods':
              // These are array fields - convert single answers to arrays
              if (typeof answer === 'string') {
                profileUpdates[question.profile_field] = [answer]
              } else if (Array.isArray(answer)) {
                profileUpdates[question.profile_field] = answer
              }
              break
            default:
              // For most fields, store the answer as-is
              console.log(`[PROFILE UPDATE] Default case - storing answer as-is for field: ${question.profile_field}`)
              profileUpdates[question.profile_field] = answer
              break
          }
        }
      })

      console.log('[PROFILE UPDATE] Final profileUpdates object:', profileUpdates)
      console.log('[PROFILE UPDATE] Number of fields to update:', Object.keys(profileUpdates).length)

      // Only update if we have fields to update
      if (Object.keys(profileUpdates).length > 0) {
        profileUpdates.updated_at = new Date().toISOString()
        
        console.log('[PROFILE UPDATE] Calling Supabase update with:', profileUpdates)
        
        const { data, error: profileError } = await supabaseClient
          .from('alumni_profiles')
          .update(profileUpdates)
          .eq('user_id', user.id)
          .select()

        if (profileError) {
          console.error('[PROFILE UPDATE] Error updating alumni profile:', profileError)
          console.error('[PROFILE UPDATE] Error details:', {
            code: profileError.code,
            message: profileError.message,
            details: profileError.details,
            hint: profileError.hint
          })
          // Don't throw here - survey submission was successful, profile update failure shouldn't break the flow
          console.warn('Survey submitted successfully but profile update failed')
        } else {
          console.log('[PROFILE UPDATE] Alumni profile updated successfully!')
          console.log('[PROFILE UPDATE] Updated data:', data)
        }
      } else {
        console.log('[PROFILE UPDATE] No profile fields to update')
      }
    } catch (error) {
      console.error('[PROFILE UPDATE] Exception during profile update:', error)
      console.error('[PROFILE UPDATE] Error stack:', error instanceof Error ? error.stack : 'No stack trace')
      // Don't throw here - survey submission was successful
    }
  }

  const renderQuestion = (question: any, index: number) => {
    const questionText = question.text || question.question || `Question ${index + 1}`
    const value = answers[question.id]

    switch (question.type) {
      case 'rating':
        const scale = question.rating_scale || 10
        return (
          <div key={question.id} className="space-y-3">
            <label className="block text-sm font-medium text-gray-900">
              {index + 1}. {questionText}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: scale }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => !isViewingResponse && handleAnswerChange(question.id, num)}
                  disabled={isViewingResponse}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    value === num
                      ? 'bg-blue-600 text-white border-blue-600'
                      : isViewingResponse
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500">Rate from 1 (lowest) to {scale} (highest)</p>
          </div>
        )

      case 'multiple_choice':
        return (
          <div key={question.id} className="space-y-3">
            <label className="block text-sm font-medium text-gray-900">
              {index + 1}. {questionText}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {question.options?.map((option: string, optIndex: number) => (
                <label
                  key={optIndex}
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="radio"
                    name={question.id}
                    value={option}
                    checked={value === option}
                    onChange={(e) => !isViewingResponse && handleAnswerChange(question.id, e.target.value)}
                    disabled={isViewingResponse}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 disabled:text-gray-400 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          </div>
        )

      case 'yes_no':
        return (
          <div key={question.id} className="space-y-3">
            <label className="block text-sm font-medium text-gray-900">
              {index + 1}. {questionText}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => !isViewingResponse && handleAnswerChange(question.id, true)}
                disabled={isViewingResponse}
                className={`px-6 py-2 rounded-lg border-2 transition-all ${
                  value === true
                    ? 'bg-green-600 text-white border-green-600'
                    : isViewingResponse
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                }`}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => !isViewingResponse && handleAnswerChange(question.id, false)}
                disabled={isViewingResponse}
                className={`px-6 py-2 rounded-lg border-2 transition-all ${
                  value === false
                    ? 'bg-red-600 text-white border-red-600'
                    : isViewingResponse
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'
                }`}
              >
                No
              </button>
            </div>
          </div>
        )

      case 'text':
      case 'textarea':
        // Skills acquired gets the SkillsPicker
        if (question.profile_field === 'skills_acquired') {
          const skillsValue = Array.isArray(value) ? value : (typeof value === 'string' && value ? value.split(',').map((s: string) => s.trim()).filter(Boolean) : [])
          return (
            <div key={question.id} className="space-y-3">
              <label className="block text-sm font-medium text-gray-900">
                {index + 1}. {questionText}
                {question.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <SkillsPicker
                value={skillsValue}
                onChange={(skills) => !isViewingResponse && handleAnswerChange(question.id, skills)}
                disabled={isViewingResponse}
                placeholder="Search skills or type a custom one…"
              />
            </div>
          )
        }
        return (
          <div key={question.id} className="space-y-3">
            <label className="block text-sm font-medium text-gray-900">
              {index + 1}. {questionText}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              value={value || ''}
              onChange={(e) => !isViewingResponse && handleAnswerChange(question.id, e.target.value)}
              disabled={isViewingResponse}
              rows={4}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isViewingResponse ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
              placeholder={isViewingResponse ? '' : 'Type your answer here...'}
            />
          </div>
        )

      case 'scale':
        const min = question.min || 1
        const max = question.max || 5
        return (
          <div key={question.id} className="space-y-3">
            <label className="block text-sm font-medium text-gray-900">
              {index + 1}. {questionText}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="range"
              min={min}
              max={max}
              value={value || min}
              onChange={(e) => !isViewingResponse && handleAnswerChange(question.id, parseInt(e.target.value))}
              disabled={isViewingResponse}
              className={`w-full ${
                isViewingResponse ? 'cursor-not-allowed opacity-60' : ''
              }`}
            />
            <div className="flex justify-between text-sm text-gray-600">
              <span>{min}</span>
              <span className="font-semibold text-blue-600">{value || min}</span>
              <span>{max}</span>
            </div>
          </div>
        )

      default:
        return (
          <div key={question.id} className="space-y-3">
            <label className="block text-sm font-medium text-gray-900">
              {index + 1}. {questionText}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => !isViewingResponse && handleAnswerChange(question.id, e.target.value)}
              disabled={isViewingResponse}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isViewingResponse ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
              placeholder={isViewingResponse ? '' : 'Type your answer here...'}
            />
          </div>
        )
    }
  }

  // If viewing a specific survey
  if (selectedSurvey) {
    const visibleQuestions = selectedSurvey.questions.filter((q: any) => isQuestionVisible(q))
    const totalQuestions = visibleQuestions.length
    const answeredQuestions = visibleQuestions.filter((q: any) => {
      const a = answers[q.id]
      return a !== '' && a !== null && a !== undefined
    }).length
    const progress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0

    return (
      <div className="space-y-6">
        <button
          onClick={() => {
            setSelectedSurvey(null)
            setAnswers({})
            navigate('/survey')
          }}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          Back to Surveys
        </button>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Header Section — matches the survey list hero banner */}
          <div className="bg-blue-900 p-6 sm:p-8 text-white relative overflow-hidden rounded-t-xl">
            <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
            <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
            <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
            <div className="relative">
              {isViewingResponse && (
                <div className="mb-2 flex items-center gap-2 text-green-300">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span className="text-sm font-semibold">Viewing Your Response</span>
                </div>
              )}
              <h1 className="text-2xl sm:text-3xl font-bold mb-1">
                {selectedSurvey.title}
              </h1>
              {selectedSurvey.description && (
                <p className="text-blue-200 text-sm mt-1">{selectedSurvey.description}</p>
              )}
              {selectedSurvey.deadline && (
                <div className="mt-3 flex items-center gap-2 text-blue-200">
                  <ClockIcon className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Deadline: {new Date(selectedSurvey.deadline).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          {!isViewingResponse && (
            <div className="px-6 pt-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Progress</span>
                <span className="text-sm font-semibold text-blue-600">
                  {answeredQuestions} of {totalQuestions} questions answered
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-yellow-400 h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Questions */}
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="p-6 space-y-5">
            {selectedSurvey.questions.filter((q: any) => isQuestionVisible(q)).map((question: any, index: number) => (
              <div key={question.id} className="bg-gray-50 rounded-xl p-5 border border-gray-200 hover:border-blue-200 transition-colors">
                {renderQuestion(question, index)}
              </div>
            ))}

            {/* Submit Section */}
            <div className="flex gap-4 pt-5 border-t border-gray-200">
              {isViewingResponse ? (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSurvey(null)
                    setAnswers({})
                    setIsViewingResponse(false)
                    navigate('/survey')
                  }}
                  className="px-8 py-2.5 rounded-lg font-bold text-sm transition-all bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-700 hover:to-gray-800 shadow-md"
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    type="submit"
                    disabled={submitting}
                    className={`px-10 py-2.5 rounded-lg font-bold text-sm transition-all ${
                      submitting
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-md'
                    }`}
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Submitting...
                      </span>
                    ) : (
                      'Submit Survey'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedSurvey(null)
                      setAnswers({})
                      navigate('/survey')
                    }}
                    className="px-8 py-2.5 rounded-lg font-bold text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Survey list view
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // Calculate stats
  const availableSurveys = surveys.filter(s => 
    !completedSurveys.includes(s.id) && s.status === 'active'
  )
  const completedCount = completedSurveys.filter(id => surveys.some(s => s.id === id)).length

  // Filter surveys based on selected filter
  const filteredSurveys = surveys.filter(survey => {
    const isCompleted = completedSurveys.includes(survey.id)
    const isExpired = isSurveyExpired(survey)
    const isDraft = survey.status === 'draft'
    
    // Available: must be active status, not completed, not expired
    if (filter === 'available') return survey.status === 'active' && !isCompleted && !isExpired
    
    // Completed: show any survey the user has completed (regardless of current status)
    if (filter === 'completed') return isCompleted
    
    // All: show everything except drafts (unless completed)
    return !isDraft || isCompleted
  })

  // Debug logging for completed surveys
  if (filter === 'completed') {
    console.log('=== COMPLETED FILTER DEBUG ===')
    console.log('All surveys in state:', surveys.map(s => ({ id: s.id, title: s.title, status: s.status })))
    console.log('Completed survey IDs from responses:', completedSurveys)
    console.log('Checking each survey:')
    surveys.forEach(s => {
      const isCompleted = completedSurveys.includes(s.id)
      console.log(`- ${s.title} (${s.status}): ID=${s.id}, isCompleted=${isCompleted}`)
    })
    console.log('Final filtered completed surveys:', filteredSurveys.map(s => ({ id: s.id, title: s.title, status: s.status })))
  }

  const handleFilterChange = (newFilter: 'all' | 'available' | 'completed') => {
    setFilter(newFilter)
    setSearchParams({ filter: newFilter })
  }

  return (
    <div className="space-y-6">
      {/* ── Hero Banner ── */}
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative">
          <h1 className="text-2xl sm:text-3xl font-bold">Surveys</h1>
          <p className="text-blue-200 text-sm mt-1">
            Help us improve our programs by completing surveys
          </p>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-3">
              <div className="bg-white/20 p-2.5 rounded-lg shrink-0">
                <ClipboardDocumentCheckIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold">{surveys.length}</p>
                <p className="text-xs text-blue-200 font-medium">Total Surveys</p>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-3">
              <div className="bg-yellow-400/30 p-2.5 rounded-lg shrink-0">
                <ClockIcon className="h-5 w-5 text-yellow-300" />
              </div>
              <div>
                <p className="text-2xl font-bold">{availableSurveys.length}</p>
                <p className="text-xs text-blue-200 font-medium">Available</p>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center gap-3">
              <div className="bg-green-400/30 p-2.5 rounded-lg shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-300" />
              </div>
              <div>
                <p className="text-2xl font-bold">{completedCount}</p>
                <p className="text-xs text-blue-200 font-medium">Completed</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Filter Tabs ── */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => handleFilterChange('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            filter === 'all'
              ? 'bg-white shadow-sm text-gray-900'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          All
        </button>
        <button
          onClick={() => handleFilterChange('available')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            filter === 'available'
              ? 'bg-white shadow-sm text-gray-900'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Available ({availableSurveys.length})
        </button>
        <button
          onClick={() => handleFilterChange('completed')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            filter === 'completed'
              ? 'bg-white shadow-sm text-gray-900'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Completed ({completedCount})
        </button>
      </div>

      {/* ── Survey List ── */}
      {filteredSurveys.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <ClipboardDocumentCheckIcon className="h-8 w-8 text-gray-300" />
          </div>
          <h3 className="text-lg font-bold text-gray-700 mb-1">
            {filter === 'available' && 'No available surveys'}
            {filter === 'completed' && 'No completed surveys yet'}
            {filter === 'all' && 'No surveys available'}
          </h3>
          <p className="text-sm text-gray-400">
            {filter === 'available' && 'All surveys have been completed. Great job!'}
            {filter === 'completed' && 'Start completing surveys to track your progress here.'}
            {filter === 'all' && 'There are currently no surveys for your program. Check back later!'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredSurveys.map((survey) => {
            const isCompleted = completedSurveys.includes(survey.id)
            const isClosed = survey.status === 'closed' || survey.status === 'archived'
            const isExpired = isSurveyExpired(survey)
            const isUnavailable = isClosed || isExpired

            // Deterministic icon color from survey title
            const iconColors = [
              'bg-blue-900 text-white', 'bg-yellow-400 text-gray-900', 'bg-indigo-600 text-white',
              'bg-emerald-600 text-white', 'bg-rose-600 text-white', 'bg-violet-600 text-white',
            ]
            const colorIdx = survey.title.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % iconColors.length

            return (
              <div
                key={survey.id}
                className={`bg-white rounded-2xl border overflow-hidden transition-all duration-200 ${
                  isCompleted
                    ? 'border-green-200 shadow-sm cursor-pointer hover:shadow-lg'
                    : isUnavailable
                    ? 'border-gray-100 shadow-sm cursor-default opacity-70'
                    : 'border-gray-100 shadow-sm hover:shadow-lg cursor-pointer'
                }`}
                onClick={() => !isUnavailable && handleSelectSurvey(survey, isCompleted)}
              >
                {/* Status Bar */}
                {isCompleted && (
                  <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-2 flex items-center gap-2 text-white text-sm font-semibold">
                    <CheckCircleIcon className="h-4 w-4" />
                    Completed
                  </div>
                )}
                {!isCompleted && isExpired && survey.deadline && (
                  <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-2 flex items-center gap-2 text-white text-sm font-semibold">
                    <ExclamationCircleIcon className="h-4 w-4" />
                    Expired — Deadline passed on {new Date(survey.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                )}
                {!isCompleted && !isExpired && isClosed && (
                  <div className="bg-gradient-to-r from-gray-500 to-gray-600 px-6 py-2 flex items-center gap-2 text-white text-sm font-semibold">
                    <XCircleIcon className="h-4 w-4" />
                    Closed by administrator
                  </div>
                )}

                {/* Content */}
                <div className="p-5 sm:p-6">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-11 h-11 rounded-xl ${iconColors[colorIdx]} flex items-center justify-center shrink-0 text-sm font-bold`}>
                      {survey.title.charAt(0).toUpperCase()}
                    </div>

                    {/* Body */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 leading-snug line-clamp-2">{survey.title}</h3>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{survey.description}</p>
                        </div>
                        {/* Badge */}
                        {isCompleted ? (
                          <span className="shrink-0 inline-flex items-center gap-1 bg-green-50 text-green-700 text-[11px] font-bold px-2.5 py-1 rounded-full border border-green-200">
                            <CheckCircleIcon className="h-3.5 w-3.5" /> Done
                          </span>
                        ) : !isUnavailable ? (
                          <span className="shrink-0 inline-flex items-center gap-1 bg-yellow-50 text-yellow-800 text-[11px] font-bold px-2.5 py-1 rounded-full border border-yellow-200">
                            <ClockIcon className="h-3.5 w-3.5" /> Open
                          </span>
                        ) : null}
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <ClipboardDocumentCheckIcon className="h-3.5 w-3.5 text-gray-400" />
                            <span className="font-medium text-gray-700">{survey.questions.length}</span> questions
                          </span>
                          {survey.deadline && (
                            <span className="flex items-center gap-1 text-amber-600 font-medium">
                              <ClockIcon className="h-3.5 w-3.5" />
                              Due {new Date(survey.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          )}
                        </div>

                        {isCompleted ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSelectSurvey(survey, true) }}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors shadow-sm hover:shadow flex-shrink-0"
                          >
                            View Response
                          </button>
                        ) : !isUnavailable ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSelectSurvey(survey, false) }}
                            className="px-4 py-2 bg-blue-900 text-white rounded-lg text-sm font-semibold hover:bg-blue-800 transition-colors shadow-sm hover:shadow flex-shrink-0"
                          >
                            Start Survey
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default SurveyPage